import { Checkbox } from '@chakra-ui/react/checkbox';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { type DataAttr } from '@chakra-ui/react/styled-system';
import { Text } from '@chakra-ui/react/text';
import { forwardRef, type InputHTMLAttributes, type ReactElement, type ReactNode, type Ref } from 'react';

import { IconCheck, IconMinus } from '../../icons';
import { isEmptyNode } from '../../utilities/isEmptyNode';

type BitkitCheckboxBaseProps = {
  /** Props forwarded to the underlying `<input>` (e.g. `data-testid`). */
  inputProps?: InputHTMLAttributes<HTMLInputElement> & DataAttr;
  state?: 'disabled' | 'readOnly' | 'skeleton';
} & Omit<Checkbox.RootProps, 'disabled' | 'readOnly'>;

/**
 * A checkbox has to end up with an accessible name, and it takes that name from its label. So either
 * pass a visible `labelText`, or — for a checkbox in a layout with no room for one, like a table
 * select-all header — an `aria-label`, which is applied to the hidden input instead of being
 * rendered.
 */
export type BitkitCheckboxProps<T extends NonNullable<ReactNode> = NonNullable<ReactNode>> = BitkitCheckboxBaseProps &
  (
    | {
        helperText?: ReactNode;
        /**
         * The visible label, which is where the checkbox gets its accessible name from. It has to be
         * non-empty — an empty string is a compile error. When the layout has no room for a visible
         * label, pass `aria-label` instead of an empty `labelText`.
         */
        labelText: T extends '' ? never : T;
      }
    | { 'aria-label': string; helperText?: never; labelText?: null }
  );

const BitkitCheckbox = forwardRef<HTMLInputElement, BitkitCheckboxProps>((props, ref) => {
  const { 'aria-label': ariaLabel, helperText, inputProps, labelText, state, ...rest } = props;
  const hasHelperText = !isEmptyNode(helperText);
  const hasLabel = !isEmptyNode(labelText) || hasHelperText;

  // Skeleton has to set disabled to be inert: the Skeleton wrapper's own pointer-events: none never
  // stopped a click, it only moved the target to the <label>, which activates the control. The cursor
  // style prop then keeps the arrow plain — style props land outside @layer recipes, where the
  // recipe's _disabled: { cursor: 'not-allowed' } sits.
  return (
    <Checkbox.Root
      cursor={state === 'skeleton' ? 'default' : undefined}
      disabled={state === 'disabled' || state === 'skeleton'}
      readOnly={state === 'readOnly'}
      {...rest}
    >
      <Checkbox.HiddenInput aria-label={ariaLabel} ref={ref} {...inputProps} />
      <Skeleton loading={state === 'skeleton'}>
        <Checkbox.Control>{rest.checked === 'indeterminate' ? <IconMinus /> : <IconCheck />}</Checkbox.Control>
      </Skeleton>
      {hasLabel && (
        <Skeleton loading={state === 'skeleton'}>
          <Checkbox.Label as="div">
            {labelText}
            {hasHelperText && (
              <Text color="text/helper" textStyle="comp/input/helperText">
                {helperText}
              </Text>
            )}
          </Checkbox.Label>
        </Skeleton>
      )}
    </Checkbox.Root>
  );
});

BitkitCheckbox.displayName = 'BitkitCheckbox';

// The generic is what makes `labelText=""` a compile error: `Exclude` cannot narrow `string` to
// "non-empty string", but a conditional type on an inferred literal can. The cast carries the
// generic through the call site, which `forwardRef`'s own signature would erase.
export default BitkitCheckbox as <T extends NonNullable<ReactNode>>(
  props: BitkitCheckboxProps<T> & { ref?: Ref<HTMLInputElement> },
) => ReactElement;
